import React, { useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ForecastResults = ({ result }) => {
  const { t } = useTranslation();
  
  if (!result) return (
    <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{t('adminForecasting.forecastResults')}</h3>
      <div className="text-red-600">
        {t('adminForecasting.notEnoughData')}
      </div>
    </div>
  );

  const hasValidData =
    result.duration !== 'Unknown' &&
    result.workers !== 'Unknown' &&
    result.hours !== 'Unknown';

  return (
    <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{t('adminForecasting.forecastResults')}</h3>
      {hasValidData ? (
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-700">{t('adminForecasting.estimatedDuration')}:</span>
            <span className="text-sm text-gray-900 font-semibold">{result.duration} days</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-700">{t('adminForecasting.estimatedWorkers')}:</span>
            <span className="text-sm text-gray-900 font-semibold">{result.workers}</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-700">{t('adminForecasting.estimatedTotalWorkingHours')}:</span>
            <span className="text-sm text-gray-900 font-semibold">{result.hours}</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-sm font-medium text-gray-700">{t('adminForecasting.notificationThresholds')}:</span>
            <span className="text-sm text-gray-900 font-semibold">{(result.notifications || []).join(', ')}%</span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const AdminForecasting = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    projectName: '',
    power: '',
    constructionType: 'Static',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.projectName.trim() || !formData.power) {
      alert(t('adminForecasting.fillRequiredFields'));
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/api/forecast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (response.ok && data.result) {
        setResult({
          duration: data.result.duration || 'Unknown',
          workers: data.result.workers || 'Unknown',
          hours: data.result.hours || 'Unknown',
          notifications: data.result.notifications || [25, 50, 75, 100],
          similarCount: typeof data.similarCount === 'number' ? data.similarCount : 0,
        });
        console.log("Similar projects found:", data.similarCount);
        if (
          data.result.duration == null &&
          data.result.workers == null &&
          data.result.hours == null
        ) {
          setResult({
            duration: 'Unknown',
            workers: 'Unknown',
            hours: 'Unknown',
            notifications: data.result.notifications || [25, 50, 75, 100],
            similarCount: typeof data.similarCount === 'number' ? data.similarCount : 0,
          });
          return;
        }
      } else {
        setResult(null);
      }
    } catch (err) {
      console.error('Forecast error:', err);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center pt-16">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('adminForecasting.projectForecasting')}</h1>
          <p className="text-gray-600 mt-2">{t('adminForecasting.generateAccurateForecasts')}</p>
        </div>
      </div>

      {/* Forecasting Form */}
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('adminForecasting.projectDetails')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminForecasting.projectName')}</label>
            <input
              type="text"
              name="projectName"
              value={formData.projectName}
              onChange={handleChange}
              placeholder={t('adminForecasting.projectNamePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminForecasting.powerCapacity')}</label>
            <input
              type="number"
              name="power"
              value={formData.power}
              onChange={handleChange}
              placeholder={t('adminForecasting.powerCapacityPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminForecasting.constructionType')}</label>
            <select
              name="constructionType"
              value={formData.constructionType}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="Static">{t('adminForecasting.static')}</option>
              <option value="Roof">{t('adminForecasting.roof')}</option>
              <option value="Tracker">{t('adminForecasting.tracker')}</option>
            </select>
          </div>
          <div className="flex items-end">
            {typeof result?.similarCount === 'number' ? (
              <p className="text-sm text-gray-600">
                {t('adminForecasting.similarProjectsFound')}: <span className="font-semibold text-blue-600">{result.similarCount}</span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex justify-end pt-4">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? t('adminForecasting.calculating') : t('adminForecasting.generateForecast')}
          </button>
        </div>
      </div>

      {/* Forecast Results */}
      <ForecastResults result={result} />
    </div>
  );
};

export default AdminForecasting;